import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  type ReactElement,
  type ReactNode,
} from "react";

import { cn, platformProps } from "../../lib";
import { useSidebarMenuContext } from "../../molecules/sidebar-nav-item/context";
import { Box } from "../../primitives";
import { sidebarMenuSectionClasses, sidebarMenuSectionListClasses } from "./lib";
import type { SidebarMenuSectionProps } from "./types";

const isSectionHeader = (child: ReactNode): child is ReactElement<{ id?: string }> => {
  if (!isValidElement(child)) return false;
  const type = child.type as { displayName?: string };
  return type?.displayName === "SectionHeader";
};

/**
 * A section block inside a `SidebarMenu`. Pairs an optional
 * `SidebarMenu.SectionHeader` with one or more `SidebarNavItem` rows.
 *
 * Header visibility is driven by `SidebarMenuContext.collapsed` — when
 * collapsed, headers don't make sense in an icon-only rail and are hidden.
 *
 * The bottom divider is rendered by the parent `SidebarMenu` (it's the only
 * place that knows which section is last).
 */
export const SidebarMenuSection = ({ children, className, testID }: SidebarMenuSectionProps) => {
  const { collapsed } = useSidebarMenuContext();
  const headingId = useId();

  const childArray = Children.toArray(children);
  const headerChild = childArray.find(isSectionHeader);
  const itemChildren = childArray.filter((child) => !isSectionHeader(child));

  const showHeader = !collapsed && headerChild != null;
  const renderedHeader = showHeader
    ? cloneElement(headerChild, { id: headerChild.props.id ?? headingId })
    : null;

  return (
    <Box
      as="section"
      {...platformProps({ testID, slot: "sidebar-menu-section" })}
      aria-labelledby={showHeader ? headingId : undefined}
      className={cn(sidebarMenuSectionClasses, collapsed && "items-center", className)}
    >
      {renderedHeader}
      <Box as="ul" className={cn(sidebarMenuSectionListClasses, collapsed && "items-center")}>
        {itemChildren}
      </Box>
    </Box>
  );
};

SidebarMenuSection.displayName = "SidebarMenuSection";
